import React, {useMemo, useState} from "react";
import {PiCheck, PiMagnifyingGlass, PiX} from "react-icons/pi";

/**
 * Mehrfachauswahl von Mitarbeitern für den Vergleich - eigens gestaltet
 * statt eines nativen <select multiple> (globale Vorgabe: keine
 * browsereigenen Bedienelemente). Reine Umschalt-Kacheln statt eines
 * Aufklappmenüs, weil bei einer Mehrfachauswahl mit typischerweise
 * überschaubarer Mitarbeiterzahl (siehe Kommentar in
 * NutzungMitarbeiterBereiche.jsx: rund 28 Personen) eine sofort sichtbare
 * Liste schneller bedienbar ist als ein Dropdown mit Checkboxen.
 *
 * @param {{value:string, label:string}[]} options
 * @param {string[]} selected - Ausgewählte Werte (als String, wie bei
 *   employeeOptions aus useNutzungFilter).
 * @param {(next:string[]) => void} onChange
 */
export default function NutzungMitarbeiterAuswahl({options, selected, onChange}) {
    const [suche, setSuche] = useState("");

    const gefiltert = useMemo(() => {
        const begriff = suche.trim().toLocaleLowerCase("de-DE");
        if (!begriff) {
            return options;
        }
        return options.filter((eintrag) => eintrag.label.toLocaleLowerCase("de-DE").includes(begriff));
    }, [options, suche]);

    const istAusgewaehlt = (wert) => selected.includes(String(wert));

    const umschalten = (wert) => {
        const text = String(wert);
        onChange(istAusgewaehlt(text) ? selected.filter((eintrag) => eintrag !== text) : [...selected, text]);
    };

    return (
        <div className="border rounded-3 p-3 bg-body">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                <div className="input-group input-group-sm" style={{maxWidth: 260}}>
                    <span className="input-group-text bg-body-secondary border-end-0">
                        <PiMagnifyingGlass aria-hidden="true" />
                    </span>
                    <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Mitarbeiter suchen …"
                        value={suche}
                        onChange={(event) => setSuche(event.target.value)}
                        aria-label="Mitarbeiter suchen"
                    />
                </div>
                {selected.length > 0 && (
                    <button type="button" className="btn btn-link btn-sm text-decoration-none px-0" onClick={() => onChange([])}>
                        <PiX className="me-1" aria-hidden="true" />
                        Auswahl leeren ({selected.length})
                    </button>
                )}
            </div>

            <div className="d-flex flex-wrap gap-2" role="group" aria-label="Mitarbeiterauswahl für den Vergleich">
                {gefiltert.map((eintrag) => {
                    const aktiv = istAusgewaehlt(eintrag.value);
                    return (
                        <button
                            key={eintrag.value}
                            type="button"
                            aria-pressed={aktiv}
                            className={`btn btn-sm rounded-pill d-inline-flex align-items-center gap-1 ${
                                aktiv ? "btn-primary" : "btn-outline-secondary"
                            }`}
                            onClick={() => umschalten(eintrag.value)}
                        >
                            {aktiv && <PiCheck aria-hidden="true" />}
                            {eintrag.label}
                        </button>
                    );
                })}
                {gefiltert.length === 0 && <span className="text-muted small">Keine Treffer.</span>}
            </div>
        </div>
    );
}
